// PresenceTooltip — tooltip que CONSULTA a presença do usuário sob demanda, ao
// abrir (hover), e mostra "Carregando informações…" até a resposta chegar.
// Mesmo comportamento/visual do frontend-v2. NÃO embute status no payload do
// user: busca fresco do backend (GET /users/:id/presence) ao ativar.

import React, { useCallback, useState } from "react";
import { Box, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

import api from "../../services/api";
import { presenceParts } from "../../helpers/presence";

function PresenceBody({ loading, error, data }) {
  if (loading || (!data && !error)) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.25 }}>
        <CircularProgress size={13} thickness={5} sx={{ color: "text.disabled" }} />
        <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
          Carregando informações…
        </Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
        Não foi possível carregar o status
      </Typography>
    );
  }

  const { label, color, relative, reason } = presenceParts(data.status, data);

  return (
    <Box sx={{ display: "inline-flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.25, color: "text.primary" }} noWrap>
        {data.name}
      </Typography>

      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box
          sx={{
            width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
            bgcolor: color,
            boxShadow: `0 0 0 3px ${alpha(color, 0.18)}`,
          }}
        />
        <Typography component="span" sx={{ fontSize: 12, lineHeight: 1, color: "text.secondary", whiteSpace: "nowrap" }}>
          <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>{label}</Box>
          {relative ? ` · ${relative}` : ""}
        </Typography>
      </Stack>

      {reason && (
        <Box
          sx={{
            px: 0.875, py: 0.5, borderRadius: "6px",
            bgcolor: alpha(color, 0.1),
            borderLeft: `2px solid ${color}`,
          }}
        >
          <Typography sx={{ fontSize: 12, lineHeight: 1.3, color: "text.secondary", fontStyle: "italic" }}>
            {reason}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default function PresenceTooltip({ userId, placement = "top", children }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const handleOpen = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(false);
    setData(null);
    try {
      const resp = await api.get(`/users/${userId}/presence`);
      setData(resp.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  return (
    <Tooltip
      onOpen={handleOpen}
      enterDelay={200}
      enterNextDelay={200}
      arrow
      placement={placement}
      title={<PresenceBody loading={loading} error={error} data={data} />}
      componentsProps={{
        tooltip: {
          sx: (theme) => ({
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "10px",
            boxShadow: theme.customShadows?.dropdown || theme.shadows[8],
            px: 1.25,
            py: 1,
            m: 0,
            maxWidth: 260,
          }),
        },
        arrow: {
          sx: (theme) => ({
            color: theme.palette.background.paper,
            "&::before": { border: `1px solid ${theme.palette.divider}` },
          }),
        },
      }}
    >
      {children}
    </Tooltip>
  );
}
